import React, { useCallback, useEffect, useRef, lazy } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface CapturesCarouselProps {
  images: string[];
}
export function CapturesCarousel({
  images
}: CapturesCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const isPausedRef = useRef(false);
  // Desplaza el carrusel una "página" (ancho del primer item) en la dirección dada
  const scrollByItem = useCallback((direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const firstItem = track.querySelector<HTMLElement>('[data-capture-item]');
    const step = firstItem ? firstItem.offsetWidth + 16 : track.clientWidth * 0.8;
    // Si llegamos al final, volvemos al inicio (loop suave)
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    if (direction === 1 && atEnd) {
      track.scrollTo({
        left: 0,
        behavior: 'smooth'
      });
      return;
    }
    const atStart = track.scrollLeft <= 4;
    if (direction === -1 && atStart) {
      track.scrollTo({
        left: track.scrollWidth,
        behavior: 'smooth'
      });
      return;
    }
    track.scrollBy({
      left: step * direction,
      behavior: 'smooth'
    });
  }, []);
  // Auto-avance lento; se pausa cuando el usuario interactúa
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPausedRef.current) scrollByItem(1);
    }, 3500);
    return () => clearInterval(interval);
  }, [scrollByItem]);
  const pause = () => {
    isPausedRef.current = true;
  };
  const resume = () => {
    // pequeño retraso para no reanudar bruscamente tras una acción manual
    window.setTimeout(() => {
      isPausedRef.current = false;
    }, 4000);
  };
  return <div className="relative max-w-6xl mx-auto" data-id="element-30">
      {/* Track desplazable: scroll nativo (drag táctil) + scroll-snap */}
      <div ref={trackRef} onMouseEnter={pause} onMouseLeave={resume} onTouchStart={pause} onTouchEnd={resume} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" data-id="element-31">
        {images.map((src, i) => <div key={i} data-capture-item className="snap-center shrink-0 w-60 sm:w-64 md:w-72 rounded-xl overflow-hidden border border-gray-800 bg-brand-dark" data-id="element-32">
            <img src={src} alt={`Captura de resultado de alumno ${i + 1}`} className="w-full h-auto object-contain select-none pointer-events-none" loading="lazy" draggable={false} data-id="element-33" />
          </div>)}
      </div>

      {/* Controles manuales */}
      <button type="button" onClick={() => {
      pause();
      scrollByItem(-1);
      resume();
    }} aria-label="Anterior" className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/70 border border-gray-700 text-white hover:bg-brand-orange hover:border-brand-orange transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange" data-id="element-34">
        <ChevronLeftIcon className="w-5 h-5" data-id="element-35" />
      </button>
      <button type="button" onClick={() => {
      pause();
      scrollByItem(1);
      resume();
    }} aria-label="Siguiente" className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/70 border border-gray-700 text-white hover:bg-brand-orange hover:border-brand-orange transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange" data-id="element-36">
        <ChevronRightIcon className="w-5 h-5" data-id="element-37" />
      </button>
    </div>;
}